import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { UIScaleManager } from '../utils/UIScaleManager';

/**
 * ScoreDisplay configuration
 */
export interface ScoreDisplayConfig {
  /** Font size */
  fontSize: number;
  /** Text color */
  textColor: number;
  /** Label color */
  labelColor: number;
  /** Background alpha */
  backgroundAlpha: number;
  /** Padding */
  padding: number;
  /** Corner radius */
  cornerRadius: number;
  /** Pulse duration in seconds */
  pulseDuration: number;
  /** Pulse scale multiplier */
  pulseScale: number;
}

/**
 * Default configuration
 */
const DEFAULT_CONFIG: ScoreDisplayConfig = {
  fontSize: 32,
  textColor: 0xffffff,
  labelColor: 0x888888,
  backgroundAlpha: 0.6,
  padding: 12,
  cornerRadius: 8,
  pulseDuration: 0.3,
  pulseScale: 1.15,
};

/**
 * ScoreDisplay - Large score number display with pulse animation
 *
 * Features:
 * - Large score number at top-left
 * - Score formatting (K, M suffixes)
 * - Pulse animation when score increases
 * - Semi-transparent background
 * - "Score" label above number
 */
export class ScoreDisplay {
  /** Main container */
  public container: Container;

  /** Configuration */
  private config: ScoreDisplayConfig;

  /** Background graphics */
  private background: Graphics;

  /** Score label text */
  private labelText: Text;

  /** Score value text */
  private scoreText: Text;

  /** Multiplier text (shown below score when > 1x) */
  private multiplierText: Text;

  /** UI Scale Manager reference */
  private scaleManager: UIScaleManager;

  /** Current total multiplier (sprint × powerup) */
  private currentMultiplier: number = 1;

  /** Current score value */
  private currentScore: number = 0;

  /** Display score (for animation) */
  private displayScore: number = 0;

  /** Target score (for animation) */
  private targetScore: number = 0;

  /** Whether display is visible */
  private visible: boolean = false;

  /** Pulse animation state */
  private pulseTimer: number = 0;
  private isPulsing: boolean = false;

  /** Score change animation */
  private scoreAnimationTimer: number = 0;
  private scoreAnimationDuration: number = 0.5;
  private scoreStartValue: number = 0;

  constructor(config: Partial<ScoreDisplayConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.scaleManager = UIScaleManager.getInstance();

    // Apply scaling to config
    const scaledFontSize = this.scaleManager.scaleFontSize(this.config.fontSize);
    const scaledPadding = this.scaleManager.scaleSpacing(this.config.padding);

    // Create container
    this.container = new Container();
    this.container.label = 'score-display';

    // Create background
    this.background = new Graphics();
    this.container.addChild(this.background);

    // Create label text
    const labelStyle = new TextStyle({
      fontFamily: 'Arial, sans-serif',
      fontSize: this.scaleManager.scaleFontSize(12),
      fill: this.config.labelColor,
    });
    this.labelText = new Text({ text: 'SCORE', style: labelStyle });
    this.labelText.position.set(scaledPadding, scaledPadding);
    this.container.addChild(this.labelText);

    // Create score text
    const scoreStyle = new TextStyle({
      fontFamily: 'Arial, sans-serif',
      fontSize: scaledFontSize,
      fontWeight: 'bold',
      fill: this.config.textColor,
      dropShadow: {
        alpha: 0.3,
        blur: 4,
        distance: 2,
      },
    });
    this.scoreText = new Text({ text: '0', style: scoreStyle });
    this.scoreText.position.set(scaledPadding, scaledPadding + this.scaleManager.scaleFontSize(16));
    this.container.addChild(this.scoreText);

    // Create multiplier text (shown below score when > 1x)
    const multiplierStyle = new TextStyle({
      fontFamily: 'Arial, sans-serif',
      fontSize: this.scaleManager.scaleFontSize(16),
      fontWeight: 'bold',
      fill: 0xffcc00, // Gold color
      dropShadow: {
        alpha: 0.3,
        blur: 2,
        distance: 1,
      },
    });
    this.multiplierText = new Text({ text: '', style: multiplierStyle });
    this.multiplierText.position.set(scaledPadding, scaledPadding + this.scaleManager.scaleFontSize(16) + scaledFontSize + 4);
    this.multiplierText.visible = false;
    this.container.addChild(this.multiplierText);

    // Draw initial background
    this.drawBackground();

    // Position at top-left with scaled padding
    const positionPadding = this.scaleManager.scaleSpacing(15);
    this.container.position.set(positionPadding, positionPadding);

    // Initially hidden
    this.container.visible = false;

    // Listen for scale changes
    this.scaleManager.addListener(() => this.applyScaling());
  }

  /**
   * Apply current scaling to all elements
   */
  private applyScaling(): void {
    const scaledFontSize = this.scaleManager.scaleFontSize(this.config.fontSize);
    const scaledPadding = this.scaleManager.scaleSpacing(this.config.padding);

    // Update label text
    (this.labelText.style as TextStyle).fontSize = this.scaleManager.scaleFontSize(12);
    this.labelText.position.set(scaledPadding, scaledPadding);

    // Update score text
    (this.scoreText.style as TextStyle).fontSize = scaledFontSize;
    this.scoreText.position.set(scaledPadding, scaledPadding + this.scaleManager.scaleFontSize(16));

    // Update multiplier text
    (this.multiplierText.style as TextStyle).fontSize = this.scaleManager.scaleFontSize(16);
    this.multiplierText.position.set(scaledPadding, scaledPadding + this.scaleManager.scaleFontSize(16) + scaledFontSize + 4);

    // Update position
    const positionPadding = this.scaleManager.scaleSpacing(15);
    this.container.position.set(positionPadding, positionPadding);

    // Redraw background
    this.drawBackground();
  }

  /**
   * Show the score display
   */
  show(): void {
    this.visible = true;
    this.container.visible = true;
  }

  /**
   * Hide the score display
   */
  hide(): void {
    this.visible = false;
    this.container.visible = false;
  }

  /**
   * Check if visible
   */
  isVisible(): boolean {
    return this.visible;
  }

  /**
   * Set score value (triggers pulse animation if score increased)
   */
  setScore(score: number, animate: boolean = true): void {
    const previousScore = this.currentScore;
    this.currentScore = score;

    if (animate && score > previousScore) {
      // Start pulse animation
      this.isPulsing = true;
      this.pulseTimer = 0;

      // Start score count-up animation
      this.scoreStartValue = this.displayScore;
      this.targetScore = score;
      this.scoreAnimationTimer = 0;
    } else {
      // Immediately set display score
      this.displayScore = score;
      this.targetScore = score;
      this.updateScoreText();
    }
  }

  /**
   * Get current score
   */
  getScore(): number {
    return this.currentScore;
  }

  /**
   * Update animation
   * @param dt Delta time in seconds
   */
  update(dt: number): void {
    if (!this.visible) return;

    // Update pulse animation
    if (this.isPulsing) {
      this.pulseTimer += dt;

      const progress = this.pulseTimer / this.config.pulseDuration;

      if (progress >= 1) {
        // End pulse
        this.isPulsing = false;
        this.scoreText.scale.set(1, 1);
      } else {
        // Pulse effect (scale up then back down)
        const pulseProgress = Math.sin(progress * Math.PI);
        const scale = 1 + (this.config.pulseScale - 1) * pulseProgress;
        this.scoreText.scale.set(scale, scale);

        // Color flash during pulse
        const flashIntensity = pulseProgress;
        const r = 255;
        const g = Math.floor(255 - flashIntensity * 100);
        const b = Math.floor(255 - flashIntensity * 155);
        (this.scoreText.style as TextStyle).fill = (r << 16) | (g << 8) | b;
      }
    } else {
      // Reset color
      (this.scoreText.style as TextStyle).fill = this.config.textColor;
    }

    // Update score count-up animation
    if (this.displayScore !== this.targetScore) {
      this.scoreAnimationTimer += dt;

      const progress = Math.min(1, this.scoreAnimationTimer / this.scoreAnimationDuration);

      // Ease out
      const easedProgress = 1 - Math.pow(1 - progress, 3);

      this.displayScore = Math.floor(
        this.scoreStartValue + (this.targetScore - this.scoreStartValue) * easedProgress
      );

      if (progress >= 1) {
        this.displayScore = this.targetScore;
      }

      this.updateScoreText();
    }
  }

  /**
   * Update score text display
   */
  private updateScoreText(): void {
    this.scoreText.text = this.formatScore(this.displayScore);
    this.drawBackground();
  }

  /**
   * Format score for display
   */
  private formatScore(score: number): string {
    if (score >= 1000000) {
      return (score / 1000000).toFixed(1) + 'M';
    } else if (score >= 10000) {
      return (score / 1000).toFixed(1) + 'K';
    } else if (score >= 1000) {
      return (score / 1000).toFixed(2) + 'K';
    }
    return score.toLocaleString();
  }

  /**
   * Draw background panel
   */
  private drawBackground(): void {
    const { padding, cornerRadius, backgroundAlpha } = this.config;

    // Calculate size based on content
    const contentWidth = Math.max(this.scoreText.width, this.multiplierText.visible ? this.multiplierText.width : 0);
    const width = Math.max(120, contentWidth + padding * 2);
    let height = this.labelText.height + this.scoreText.height + padding * 2 + 4;

    // Add height for multiplier text if visible
    if (this.multiplierText.visible) {
      height += this.multiplierText.height + 4;
    }

    const g = this.background;
    g.clear();

    // Draw rounded rectangle background
    g.roundRect(0, 0, width, height, cornerRadius);
    g.fill({ color: 0x0a0a1a, alpha: backgroundAlpha });
  }

  /**
   * Handle screen resize (position adjustment if needed)
   */
  onResize(_width: number, _height: number): void {
    // Score display stays at top-left, no adjustment needed
    // But could be used for responsive sizing in the future
  }

  /**
   * Trigger a pulse animation manually
   */
  pulse(): void {
    this.isPulsing = true;
    this.pulseTimer = 0;
  }

  /**
   * Add score (convenience method)
   */
  addScore(amount: number): void {
    this.setScore(this.currentScore + amount);
  }

  /**
   * Reset score to zero
   */
  reset(): void {
    this.currentScore = 0;
    this.displayScore = 0;
    this.targetScore = 0;
    this.currentMultiplier = 1;
    this.multiplierText.visible = false;
    this.updateScoreText();
  }

  /**
   * Set the total multiplier (sprint × powerup)
   * Shows the multiplier below score when > 1x
   */
  setMultiplier(totalMultiplier: number): void {
    this.currentMultiplier = totalMultiplier;

    if (totalMultiplier > 1) {
      // Format multiplier text with color based on tier
      const multiplierStr = totalMultiplier.toFixed(1).replace(/\.0$/, '') + '×';
      this.multiplierText.text = `${multiplierStr} MULTIPLIER`;
      this.multiplierText.visible = true;

      // Color based on multiplier tier
      let color = 0xffcc00; // Default gold
      if (totalMultiplier >= 30) {
        color = 0xff00ff; // Magenta (legendary tier)
      } else if (totalMultiplier >= 15) {
        color = 0xff6600; // Orange
      } else if (totalMultiplier >= 8) {
        color = 0xff9900; // Gold-orange
      } else if (totalMultiplier >= 4) {
        color = 0xffcc00; // Gold
      } else if (totalMultiplier >= 2) {
        color = 0xccff00; // Yellow-green
      }
      (this.multiplierText.style as TextStyle).fill = color;
    } else {
      this.multiplierText.visible = false;
    }

    this.drawBackground();
  }

  /**
   * Get current multiplier
   */
  getMultiplier(): number {
    return this.currentMultiplier;
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.background.destroy();
    this.labelText.destroy();
    this.scoreText.destroy();
    this.multiplierText.destroy();
    this.container.destroy({ children: true });
  }
}
